import { useEffect, useState } from 'react'
import { api, daysUntil, shortDate, type Assessment, type Score, type ScoreStep, type InboundMessage, type Vendor, type VendorMessage } from '../lib/api'
import { BandFactors, stepLabel } from './BandFactors'
import { bandColour, bandWord } from './BandChip'
import { Dialog, Status, Toggletip, type Tone } from './kit'
import { VendorObservations } from './VendorObservations'
import { markDeciding, type MarkedStep } from '../lib/bandLine'
import '../styles/band-line.css'

// The newest message to this vendor's contacts and what became of it. "Was
// the vendor actually told" is asked while deciding whether to chase them, and
// a sent email is not the same answer as a WhatsApp the contact has read. One
// line each, the outcome as a status, the detail behind the (i).
function LastMessage({ vendorId }: { vendorId: string }) {
  const [message, setMessage] = useState<VendorMessage | null>(null)
  const [reply, setReply] = useState<InboundMessage | null>(null)
  useEffect(() => {
    let live = true
    api
      .vendorMessages(vendorId)
      .then((r) => {
        if (!live) return
        setMessage(r.messages[0] ?? null)
        setReply(r.inbound?.[0] ?? null)
      })
      .catch(() => {
        if (live) setMessage(null)
      })
    return () => {
      live = false
    }
  }, [vendorId])
  if (!message && !reply) return null
  return (
    <div className="vd-lastmsg vd-lastmsg-line">
      {message && (
        <p className="vd-inline">
          <span className="vd-quiet">Last message</span>
          <Status tone={outcomeTone(message.status)}>{outcomeWord(message.status)}</Status>
          <span className="vd-quiet">
            {channelWord(message.channel)}, {shortDate(message.readAt ?? message.deliveredAt ?? message.createdAt)}
          </span>
          <Toggletip label="About the last message">
            {tip(
              capital(messageKind(message.templateKey)) +
                ' by ' +
                channelWord(message.channel) +
                ', ' +
                messageState(message) +
                '.' +
                (message.status === 'FAILED' && message.detail ? ' ' + message.detail : ''),
            )}
          </Toggletip>
        </p>
      )}
      {reply && (
        <p className="vd-inline">
          <span className="vd-quiet">Last reply</span>
          <Status tone="info">{channelWord(reply.channel)}</Status>
          <span className="vd-quiet">{shortDate(reply.receivedAt)}</span>
          <Toggletip label="About the last reply">{tip(capital(replyWhat(reply).replace(/,$/, '.')) + ' ' + capital(filedWhere(reply)))}</Toggletip>
        </p>
      )}
    </div>
  )
}

function capital(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// A toggletip holds 200 characters. The whole message is on the Messages
// section, so a longer one is cut here rather than overflowing the bubble.
function tip(s: string): string {
  return s.length > 200 ? s.slice(0, 197) + '...' : s
}

function outcomeWord(status: string): string {
  switch (status) {
    case 'READ':
      return 'Read'
    case 'DELIVERED':
      return 'Delivered'
    case 'SENT':
      return 'Sent'
    case 'MUTED':
      return 'Not sent'
    case 'SUPPRESSED':
      return 'Held back'
    case 'BOUNCED':
      return 'Bounced'
    case 'COMPLAINED':
      return 'Reported'
    default:
      return 'Not delivered'
  }
}

function outcomeTone(status: string): Tone {
  switch (status) {
    case 'READ':
    case 'DELIVERED':
      return 'ok'
    case 'SENT':
      return 'info'
    case 'MUTED':
    case 'SUPPRESSED':
      return 'neutral'
    default:
      return 'crit'
  }
}

export function replyWhat(m: InboundMessage): string {
  if (m.kind === 'DOCUMENT' || m.kind === 'IMAGE') return 'a document' + (m.fileName ? ', ' + m.fileName : '') + ','
  if (m.kind === 'TEXT') return '"' + (m.text ?? '').slice(0, 120) + '",'
  return 'a message,'
}

export function filedWhere(m: InboundMessage): string {
  switch (m.filedAs) {
    case 'CLARIFICATION_REPLY':
      return 'recorded as the answer to the question that was waiting.'
    case 'RESPONSE_EVIDENCE':
      return 'attached as evidence to the answer a question was about.'
    case 'VENDOR_DOCUMENT':
      return 'filed with the vendor documents, waiting for somebody to accept it.'
    case 'ASSESSMENT_MESSAGE':
      return 'kept against the open assessment.'
    default:
      return 'kept for somebody to read.' + (m.detail ? ' ' + m.detail : '')
  }
}

export function messageKind(key: string): string {
  const map: Record<string, string> = {
    'vendor.invite': 'the invitation',
    'vendor.reminder': 'a reminder',
    'vendor.clarification': 'a question about an answer',
    'vendor.otp': 'a sign-in code',
  }
  return map[key] ?? 'a message'
}

export function channelWord(channel: string): string {
  if (channel === 'WHATSAPP') return 'WhatsApp'
  if (channel === 'SMS') return 'SMS'
  return 'email'
}

export function messageState(m: VendorMessage): string {
  switch (m.status) {
    case 'READ':
      return 'read ' + shortDate(m.readAt ?? m.createdAt)
    case 'DELIVERED':
      return 'delivered ' + shortDate(m.deliveredAt ?? m.createdAt)
    case 'SENT':
      return 'sent ' + shortDate(m.createdAt) + ', not yet confirmed as delivered'
    case 'MUTED':
      return 'not sent, because no carrier is set up for it'
    case 'SUPPRESSED':
      return 'held back, because this contact must not be written to'
    case 'BOUNCED':
      return 'bounced'
    case 'COMPLAINED':
      return 'reported as unwanted'
    default:
      return 'not delivered'
  }
}

// The vendor's risk in one panel: the band, the last message with the vendor,
// and why the band is what it is.
//
// # WHY THERE IS NO "WHAT TO DO NEXT" ANY MORE
//
// The product officer's review of 21 September asked for it to go. What it
// carried that mattered was where the questionnaire stands and the one action
// on it, resending the invitation, and both are now a line under the band with
// the last message and reply beside them. The rest pointed at screens the menu
// already opens. "Why this band" is the factors the band matrix read and every
// rule that fired, and the engine's own steps, citations included, stay whole
// in the drawer behind "How the band was reached".

const REVIEW = new Set(['SUBMITTED', 'UNDER_REVIEW'])
const SCORED = new Set(['SCORED', 'ACCEPTED'])

// The Risk tab's first part: the risk position, and under it the website
// checks, moved here from the overview (T11 of the review of 21 September,
// decision 3 of the wave 3 design). A failed check is a finding on this vendor,
// so what the supplier's own website shows is part of why the band is what it
// is, and this is where that is read.
export function VendorRiskSummary({ vendor, assessments }: { vendor: Vendor; assessments: Assessment[] }) {
  return (
    <>
      <RiskPosition vendor={vendor} assessments={assessments} />
      <VendorObservations vendorId={vendor.id} />
    </>
  )
}

function RiskPosition({ vendor, assessments }: { vendor: Vendor; assessments: Assessment[] }) {
  const [score, setScore] = useState<Score | null | undefined>(undefined)
  const [from, setFrom] = useState<Assessment | null>(null)
  const [explaining, setExplaining] = useState(false)

  // The newest assessment that carries a score. Scored and accepted ones
  // first, then anything submitted or provisional, because the band on the
  // vendor may have come from a score computed on an answer set that is
  // still formally under review.
  const candidates = [...assessments]
    .filter((a) => SCORED.has(a.status) || REVIEW.has(a.status) || a.status === 'PROVISIONAL')
    .sort((a, b) => Number(SCORED.has(b.status)) - Number(SCORED.has(a.status)) || byNewest(a, b))
    .slice(0, 3)
  const candidateKey = candidates.map((a) => a.id).join(',')

  useEffect(() => {
    let live = true
    if (candidates.length === 0) {
      setScore(null)
      setFrom(null)
      return
    }
    ;(async () => {
      for (const c of candidates) {
        try {
          const d = await api.assessment(c.id)
          if (!live) return
          if (d.score) {
            setScore(d.score)
            setFrom(d.assessment)
            return
          }
        } catch {
          /* try the next one */
        }
      }
      if (live) setScore(null)
    })()
    return () => {
      live = false
    }
  }, [candidateKey])

  const band = vendor.currentBand
  const due = daysUntil(vendor.nextDueOn)
  // The step that decided the band is marked where the steps are read, and
  // it is the one the band's line names (release 3.3, N2).
  const shown: ScoreStep[] = (score?.explanation ?? []).filter((s) => s.step !== 'inputs')
  const steps = markDeciding(shown, score?.explanation, vendor.bandReason)

  return (
    <section className="panel vd-risk" aria-labelledby="vd-riskpos-h">
      <h2 id="vd-riskpos-h" className="vh">
        Risk position
      </h2>
      <div className="vd-risk-top vd-risk-head">
        <div className="vd-bandbig">
          <span className="vd-bandword" style={{ color: bandColour(band) }}>
            {bandWord(band)}
          </span>
          {from && (
            <span className="vd-quiet">
              From <span className="kit-mono">{from.reference}</span>
              {score?.computedAt ? ', scored ' + shortDate(score.computedAt) : ''}
            </span>
          )}
          {score === undefined && candidates.length > 0 && <span className="vd-quiet">Reading the score</span>}
        </div>
        <dl className="vd-stats vd-stats-one">
          <Stat label="Next reassessment" value={vendor.nextDueOn ? shortDate(vendor.nextDueOn) : ''} text late={due != null && due < 0} />
        </dl>
      </div>

      {/* Where the questionnaire stands is read on the Overview and acted on in
          the Next step bar (release 3.2, removal items 1 and 2). This keeps the
          last message and reply, which nothing else on the page shows. */}
      <div className="vd-qstatus">
        <LastMessage vendorId={vendor.id} />
      </div>

      <div className="vd-risk-why">
        <h3 className="vd-h3">Why this band</h3>
        {vendor.bandReason?.line && <p className="band-why-lead">{vendor.bandReason.line}</p>}
        {score === null && <p className="vd-quiet">No score on file yet</p>}
        {score && <BandFactors score={score} />}
        {score?.blockApproval && (
          <p className="vd-inline vd-block">
            <Status tone="crit">Blocks activation</Status>
            <Toggletip hover label="About a blocking rule">
              The vendor cannot go live until the rule is cleared or a risk acceptance is approved.
            </Toggletip>
          </p>
        )}
        {score?.overrideBand && (
          <p className="vd-inline vd-block">
            <Status tone="special">{'Overridden to ' + bandWord(score.overrideBand)}</Status>
            {score.overrideReason && (
              <Toggletip hover label="Why the band was overridden">
                {tip(score.overrideReason)}
              </Toggletip>
            )}
          </p>
        )}
        {score && steps.length > 0 && (
          <>
            <button type="button" className="linkish vd-howlink" onClick={() => setExplaining(true)}>
              How the band was reached
            </button>
            <Dialog shape="drawer" kind="record" open={explaining} onClose={() => setExplaining(false)} title="How the band was reached">
              <Explanation steps={steps} score={score} />
            </Dialog>
          </>
        )}
      </div>
    </section>
  )
}

export function Explanation({ steps, score }: { steps: MarkedStep[]; score: Score }) {
  return (
    <div className="vd-explain">
      <ol>
        {steps.map((s, i) => (
          <li key={i} className={s.decided ? 'band-decided' : undefined}>
            <strong>
              {stepLabel(s.step)}
              {s.gateKey && <span className="kit-mono"> {s.gateKey}</span>}
            </strong>
            {s.decided && (
              <>
                {' '}
                <Status tone="info">Decided the band</Status>
              </>
            )}
            <p>{s.detail}</p>
            {s.citation && <p className="vd-quiet">{s.citation}</p>}
          </li>
        ))}
      </ol>
      {score.gatesFired.length > 0 && (
        <p className="vd-quiet">
          Rules fired: {score.gatesFired.join(', ')}
          {score.needsException.length > 0 ? '. Needing a written risk acceptance: ' + score.needsException.join(', ') : ''}
        </p>
      )}
    </div>
  )
}

function Stat({ label, value, unit, text, late }: { label: string; value: string; unit?: string; text?: boolean; late?: boolean }) {
  return (
    <div className={'vd-stat' + (text ? ' vd-stat-text' : '') + (late ? ' vd-late' : '')}>
      <dt>{label}</dt>
      <dd>
        {value || <span className="vd-quiet">-</span>}
        {/* A unit that starts with a slash is read with the number, 70/100,
            and takes no space before it. */}
        {value && unit && <small>{unit.startsWith('/') ? unit : ' ' + unit}</small>}
      </dd>
    </div>
  )
}

function byNewest(a: Assessment, b: Assessment): number {
  return (b.submittedAt ?? b.issuedAt ?? '').localeCompare(a.submittedAt ?? a.issuedAt ?? '')
}
